// src/components/layout/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import { NAVIGATION_LINKS } from '../../utils/constants';
import { createAnchorClickHandler } from '../../utils/scrollUtils';
import MobileMenu from './MobileMenu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRefs = useRef({});
  const location = useLocation();
  
  // Handle scroll event to change header style when scrolled
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && 
          dropdownRefs.current[activeDropdown] && 
          !dropdownRefs.current[activeDropdown].contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);
  
  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  // Toggle dropdown
  const toggleDropdown = (e, dropdownName) => {
    e.preventDefault();
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  // About dropdown items
  const aboutDropdownItems = [
    { name: 'Our Story', path: '/about#our-story', id: 'our-story' },
    { name: 'Vision & Mission', path: '/about#vision-mission', id: 'vision-mission' },
    { name: 'Impact on SDGs', path: '/about#sdgs-section', id: 'sdgs-section' },
    { name: 'Team', path: '/about#team-section', id: 'team-section' },
  ];
  
  // Programs dropdown items
  const programsDropdownItems = [
    { name: 'Green Skills & Climate', path: '/programs?category=green', id: 'green' },
    { name: 'Digital Literacy', path: '/programs?category=digital', id: 'digital' },
    { name: 'Engineering & Design', path: '/programs?category=engineering', id: 'engineering' },
    { name: 'Entrepreneurship', path: '/programs?category=entrepreneurship', id: 'entrepreneurship' },
  ];
  
  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-white shadow-md transition-all duration-300 py-4">
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img 
            src="/images/logo/TBLogo.png" 
            alt="Afrovate Tech Hub Logo" 
            className="h-10 md:h-12 lg:h-14"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/images/logo/leotech-logo-fallback.png";
              
              // If fallback fails, show text logo
              e.target.onerror = () => {
                e.target.outerHTML = `<div class="text-gray-800 font-montserrat font-bold text-xl sm:text-2xl">LEOTECH</div>`;
              };
            }}
          />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden sm:flex items-center space-x-2 md:space-x-6">
          {NAVIGATION_LINKS.map((link) => {
            // Check if this link matches the current path
            const isActive = location.pathname === link.path || 
                           (link.path !== '/' && location.pathname.startsWith(link.path));
            
            // Special handling for About link with dropdown
            if (link.name === 'About') {
              return (
                <div 
                  key={link.path} 
                  className="relative" 
                  ref={el => dropdownRefs.current['about'] = el}
                >
                  <a
                    href="#"
                    onClick={(e) => toggleDropdown(e, 'about')}
                    className={`text-sm md:text-base font-medium hover:text-secondary transition-colors flex items-center ${
                      isActive 
                        ? 'text-primary font-semibold'
                        : 'text-gray-800'
                    }`}
                  >
                    {link.name}
                    <FaChevronDown className={`ml-1 h-3 w-3 transition-transform ${activeDropdown === 'about' ? 'rotate-180' : ''}`} />
                  </a>
                  
                  {/* About Dropdown Menu */}
                  {activeDropdown === 'about' && (
                    <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        <Link 
                          to={link.path} 
                          className={`block px-4 py-2 text-sm hover:bg-gray-100 ${
                            isActive && !location.hash ? 'text-primary font-semibold' : 'text-gray-700'
                          }`}
                          onClick={() => setActiveDropdown(null)}
                        >
                          Overview
                        </Link>
                        
                        {aboutDropdownItems.map((item, index) => {
                          // Check if this section is active
                          const isSectionActive = isActive && location.hash === `#${item.id}`;
                          
                          // For smooth scrolling when already on the about page
                          if (location.pathname === '/about') {
                            return (
                              <a
                                key={index}
                                href={`#${item.id}`}
                                className={`block px-4 py-2 text-sm hover:bg-gray-100 ${
                                  isSectionActive ? 'text-primary font-semibold' : 'text-gray-700'
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActiveDropdown(null);
                                  
                                  const elementId = item.id;
                                  const element = document.getElementById(elementId);
                                  
                                  if (element) {
                                    const headerOffset = 100; // Adjust based on your header height
                                    const elementPosition = element.getBoundingClientRect().top;
                                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                                    
                                    window.scrollTo({
                                      top: offsetPosition,
                                      behavior: 'smooth'
                                    });
                                    
                                    // Update URL without triggering a page reload
                                    window.history.pushState(null, '', `#${elementId}`);
                                  }
                                }}
                              >
                                {item.name}
                              </a>
                            );
                          }
                          
                          // Regular link to about page with hash for other pages
                          return (
                            <Link
                              key={index}
                              to={item.path}
                              className={`block px-4 py-2 text-sm hover:bg-gray-100 ${
                                isSectionActive ? 'text-primary font-semibold' : 'text-gray-700'
                              }`}
                              onClick={() => setActiveDropdown(null)}
                            >
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            
            // Special handling for Programs link with dropdown
            if (link.name === 'Programs') {
              return (
                <div 
                  key={link.path} 
                  className="relative" 
                  ref={el => dropdownRefs.current['programs'] = el}
                >
                  <a
                    href="#"
                    onClick={(e) => toggleDropdown(e, 'programs')}
                    className={`text-sm md:text-base font-medium hover:text-secondary transition-colors flex items-center ${
                      isActive 
                        ? 'text-primary font-semibold'
                        : 'text-gray-800'
                    }`}
                  >
                    {link.name}
                    <FaChevronDown className={`ml-1 h-3 w-3 transition-transform ${activeDropdown === 'programs' ? 'rotate-180' : ''}`} />
                  </a>
                  
                  {/* Programs Dropdown Menu */}
                  {activeDropdown === 'programs' && (
                    <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        <Link 
                          to={link.path} 
                          className={`block px-4 py-2 text-sm hover:bg-gray-100 ${
                            isActive && !location.search ? 'text-primary font-semibold' : 'text-gray-700'
                          }`}
                          onClick={() => setActiveDropdown(null)}
                        >
                          All Programs
                        </Link>
                        
                        {programsDropdownItems.map((item, index) => {
                          // Check if this category is active
                          const isCategoryActive = isActive && location.search === `?category=${item.id}`;
                          
                          return (
                            <Link
                              key={index}
                              to={item.path}
                              className={`block px-4 py-2 text-sm hover:bg-gray-100 ${
                                isCategoryActive ? 'text-primary font-semibold' : 'text-gray-700'
                              }`}
                              onClick={() => setActiveDropdown(null)}
                            >
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            
            // Regular link
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm md:text-base font-medium hover:text-secondary transition-colors ${
                  isActive ? 'text-primary font-semibold' : 'text-gray-800'
                }`}
              >
                {link.name}
              </Link>
            );
          })}
          
          {/* CTA Button - Medium screens and up */}
          <Link
            to="/apply"
            className="hidden md:inline-flex items-center px-3 py-1 lg:px-4 lg:py-2 text-sm lg:text-base rounded-md font-medium transition duration-300 bg-primary text-white hover:bg-primary-dark"
          >
            Apply Now
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3 md:h-4 md:w-4 ml-1 md:ml-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </Link>
        </nav>

        {/* Small Screen CTA Button */}
        <Link
          to="/apply"
          className="sm:hidden inline-flex items-center px-2 py-1 text-xs rounded-md font-medium transition duration-300 bg-primary text-white hover:bg-primary-dark"
        >
          Apply
        </Link>
        
        {/* Mobile Menu Button */}
        <button
          onClick={toggleMenu}
          className="sm:hidden focus:outline-none ml-2"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <FaTimes className="text-gray-800" size={20} />
          ) : (
            <FaBars className="text-gray-800" size={20} />
          )}
        </button>
      </div>
      
      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={isMenuOpen} 
        onClose={() => setIsMenuOpen(false)} 
        aboutDropdownItems={aboutDropdownItems}
        programsDropdownItems={programsDropdownItems} 
      />
    </header>
  );
};

export default Header;